import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../../context/AuthContext";
import SignUpNavbar from "../../components/SignUpNavbar";
import { User, WarningCircle } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import useLivenessCheck from "../../hooks/useLivenessCheck";
import useCreateLivenessSession from "../../hooks/useCreateLivenessSession";
import useMarkLivenessVerified from "../../hooks/useMarkLivenessVerified";
import { toast } from "sonner";
import { LIVENESS_DEFERRED_KEY } from "../../components/CompleteSetupTaskList";

const SKIP_DELAY_MS = 45_000; // 45 seconds before showing skip option

const Liveness: React.FC = () => {
  const { user, refetch, setUser, isFetchingUser } = useAuth();
  const [showSkipModal, setShowSkipModal] = useState(false);
  const skipTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Start the skip timer when the page mounts
  useEffect(() => {
    skipTimerRef.current = setTimeout(() => {
      setShowSkipModal(true);
    }, SKIP_DELAY_MS);
    return () => {
      if (skipTimerRef.current) clearTimeout(skipTimerRef.current);
    };
  }, []);

  const searchParams = new URLSearchParams(window.location.search);
  const redirect = searchParams.get("redirect");

  const navigate = useNavigate();
  const { markLivenessVerified, isMarkingLiveness } = useMarkLivenessVerified();

  const { createSession, isCreatingSession, sessionError, clearSessionError } =
    useCreateLivenessSession();

  const [session, setSession] = useState<{
    sessionId: string;
    sessionToken: string;
  } | null>(null);

  const liveness = useLivenessCheck({
    sessionId: session?.sessionId ?? "",
    sessionToken: session?.sessionToken ?? "",
    allowAudio: true,
    user: user
      ? {
          firstName: user.firstname || "",
          lastName: user.lastname || "",
          email: user.email || "",
        }
      : undefined,
    onSuccess: () => {
      // Call dedicated atomic backend endpoint that ONLY updates verified_face
      markLivenessVerified(undefined, {
        onSuccess: async () => {
          const res = await refetch();
          if (res?.data?.data) {
            setUser(res.data.data);
          }
          navigate(redirect || "/home");
        },
        onError: async (err: any) => {
          console.error("Failed to save liveness status", err);
          toast.error(
            err?.message || "Oops! Liveness was verified but failed to save. Please contact support."
          );
          // Redirect anyway since local check passed
          const res = await refetch();
          if (res?.data?.data) {
            setUser(res.data.data);
          }
          navigate(redirect || "/home");
        }
      });
    },
  });

  const handleStart = async () => {
    clearSessionError();
    // Always fetch a fresh session before each attempt
    try {
      const newSession = await createSession();
      setSession(newSession);
      // Start SDK after session is set — use the values directly
      // (liveness.start reads from optionsRef which will have stale session until re-render,
      //  so we trigger after state updates via a small wrapper)
      setTimeout(() => liveness.start(), 0);
    } catch {
      // sessionError is already set by the hook
    }
  };

  const isStarting =
    isFetchingUser || isCreatingSession || liveness.status === "running" || isMarkingLiveness;

  const displayError = sessionError || liveness.error;

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <SignUpNavbar />

      <div className="p-6 md:p-10 flex justify-center items-start w-full">
        <div className="w-full max-w-2xl border rounded-xl p-6 md:p-8 shadow-sm flex flex-col">
          <div className="p-5 bg-gray-50 rounded-full self-start">
            <User className="text-visaro-primary-700" size={48} weight="bold" />
          </div>
          <header className="my-6">
            <h1 className="text-2xl md:text-3xl font-bold text-deepBlue">
              Liveness Verification
            </h1>
            <p className="text-gray-600 mt-2">
              To keep your account safe and meet regulatory (KYC) requirements,
              we need to confirm you're a real, live person. This is a quick,
              check and usually takes less than a minute.
            </p>
          </header>

          {/* What you'll need */}
          <section className="mb-6">
            <h2 className="font-semibold text-lg mb-2">What you'll need</h2>
            <ul className="list-disc list-inside text-gray-700 space-y-1">
              <li>A device with a working camera</li>
              <li>Good lighting (avoid backlight)</li>
              <li>Remove hats, masks, and sunglasses</li>
              <li>Hold your phone steady and follow the on‑screen prompts</li>
            </ul>
          </section>

          {/* Troubleshooting */}
          <section className="mb-6">
            <h2 className="font-semibold text-lg mb-2">Troubleshooting</h2>
            <ul className="list-disc list-inside text-gray-700 space-y-1">
              <li>
                If prompted, allow camera access in your browser or device
                settings.
              </li>
              <li>Ensure you're not on a very slow or unstable network.</li>
              <li>
                Try switching to another supported browser (Chrome, Safari,
                Edge, Firefox).
              </li>
            </ul>
          </section>

          {displayError && (
            <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {displayError}
            </div>
          )}

          {/* CTA */}
          <button
            className="p-4 px-5 w-full border-visaro-gray-700 border rounded-lg font-bold bg-deepBlue text-white text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleStart}
            disabled={isStarting || !user}
          >
            {isFetchingUser
              ? "Loading..."
              : isCreatingSession
              ? "Preparing..."
              : liveness.status === "running" || isMarkingLiveness
              ? "Starting..."
              : "Start"}
          </button>

          {/* Status messages */}
          {liveness.status === "success" && (
            <div className="mt-4 text-green-700 text-sm border border-green-200 bg-green-50 rounded p-3">
              Liveness verification completed successfully. You can proceed to
              the next step.
            </div>
          )}
          {liveness.status === "failed" && (
            <div className="mt-4 text-red-700 text-sm border border-red-200 bg-red-50 rounded p-3">
              We couldn't complete your liveness verification. Please try again.
            </div>
          )}

          {/* Help */}
          <footer className="mt-8 text-sm text-gray-500">
            Need help? Contact{" "}
            <a
              href="mailto:support@visaro.ng"
              className="underline text-deepBlue"
            >
              support@visaro.ng
            </a>
            .
          </footer>
        </div>
      </div>

      {/* Skip / Defer Modal */}
      {(showSkipModal || liveness.status === "failed") && (
        <div className="fixed inset-0 bg-black/40 z-[99999] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-lg flex flex-col items-center text-center">
            <div className="p-4 bg-orange-50 rounded-full mb-4">
              <WarningCircle className="text-visaro-orange" size={40} weight="fill" />
            </div>
            <h2 className="text-xl font-bold text-deepBlue mb-2">
              Having trouble?
            </h2>
            <p className="text-gray-600 mb-6">
              Don't worry — you can come back to the liveness check at any time.
              It won't block you from using your account.
            </p>
            <div className="flex flex-col gap-3 w-full">
              <button
                className="p-3 w-full rounded-lg font-bold bg-deepBlue text-white text-sm"
                onClick={() => {
                  localStorage.setItem(LIVENESS_DEFERRED_KEY, "1");
                  navigate(redirect || "/home");
                }}
              >
                Continue to Dashboard
              </button>
              <button
                className="p-3 w-full rounded-lg font-bold border border-gray-300 text-deepBlue text-sm"
                onClick={() => {
                  setShowSkipModal(false);
                  liveness.reset();
                }}
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Liveness;
